import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 bg-orange-500 rounded-sm flex items-center justify-center">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-lg">Corbel CRM</span>
          </Link>
          <Link to="/login">
            <Button variant="outline" size="sm" className="rounded-sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to App
            </Button>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-muted-foreground mb-8">Last updated: May 21, 2026</p>

        <div className="prose prose-slate max-w-none space-y-6">
          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              By accessing or using Corbel CRM ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service. These Terms apply to all visitors, users, and others who access or use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 leading-relaxed">
              Corbel CRM is a customer relationship management platform designed for residential construction companies. The Service provides tools for lead management, project tracking, team collaboration, client communication, document management, cost estimating, job-based invoicing, expense tracking, and business operations.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">3. User Accounts</h2>
            <p className="text-slate-600 leading-relaxed mb-4">When you create an account with us, you must:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities that occur under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              You must be at least 18 years old to use this Service. By using the Service, you represent and warrant that you are at least 18 years of age.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">4. Subscription and Payment</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Some features of the Service require a paid subscription. By subscribing to a paid plan:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>You agree to pay all fees associated with your selected plan</li>
              <li>Subscriptions are billed in advance on a monthly basis</li>
              <li>You authorize us to charge your payment method on file</li>
              <li>Prices are subject to change with 30 days notice</li>
              <li>Refunds are handled on a case-by-case basis</li>
            </ul>
            <p className="text-slate-600 leading-relaxed mt-4">
              <strong>Free Trial:</strong> We may offer a free trial period. At the end of the trial, you will be required to subscribe to continue using premium features.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">5. Acceptable Use</h2>
            <p className="text-slate-600 leading-relaxed mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Upload malicious code, viruses, or harmful data</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Use the Service for any illegal or unauthorized purpose</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">6. Your Data</h2>
            <p className="text-slate-600 leading-relaxed">
              You retain ownership of all data you submit to the Service ("Your Data"). By using the Service, you grant us a limited license to use, store, and process Your Data solely to provide the Service to you. You are responsible for maintaining backups of Your Data. We are not liable for any loss or corruption of Your Data.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">7. Third-Party Integrations</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service may integrate with third-party services (e.g., Google Calendar, Gmail, Stripe, Amazon Web Services). Your use of these integrations is subject to the respective third-party terms and privacy policies. We are not responsible for the practices of third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">7a. Cost Estimates & Financial Data</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              The Service allows you to import, create, and manage cost estimates and invoices. You acknowledge and agree that:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Cost data imported from third-party estimating tools (e.g., Craftsman National Estimator) is provided as a reference only. Actual costs vary by location, time, and market conditions.</li>
              <li>You are solely responsible for verifying all estimates, invoices, and financial calculations before presenting them to clients or relying on them for business decisions.</li>
              <li>Corbel CRM is not a licensed accounting, tax, or financial advisory service. The billing and expense tracking features are management tools, not substitutes for professional accounting advice.</li>
              <li>We are not liable for any financial losses resulting from inaccurate estimates, invoicing errors, or business decisions made based on data in the platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">7b. File Storage</h2>
            <p className="text-slate-600 leading-relaxed">
              Files, photos, and documents uploaded to the Service are stored in secure cloud storage (Amazon Web Services S3). Images may be automatically compressed and converted to standard formats for web compatibility. Original full-resolution versions may be retained. You are responsible for maintaining your own backups of critical files. We are not liable for any loss or corruption of uploaded files.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">8. Intellectual Property</h2>
            <p className="text-slate-600 leading-relaxed">
              The Service and its original content, features, and functionality are owned by Corbel CRM and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of the Service without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-slate-600 leading-relaxed">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. YOU USE THE SERVICE AT YOUR OWN RISK.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">10. Limitation of Liability</h2>
            <p className="text-slate-600 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, CORBEL CRM SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OR INABILITY TO USE THE SERVICE.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">11. Indemnification</h2>
            <p className="text-slate-600 leading-relaxed">
              You agree to indemnify and hold harmless Corbel CRM and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses arising out of your use of the Service or violation of these Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">12. Termination</h2>
            <p className="text-slate-600 leading-relaxed">
              We may terminate or suspend your account immediately, without prior notice, for any reason, including breach of these Terms. Upon termination, your right to use the Service will immediately cease. You may cancel your account at any time through your account settings.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">13. Changes to Terms</h2>
            <p className="text-slate-600 leading-relaxed">
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">14. Governing Law</h2>
            <p className="text-slate-600 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mt-8 mb-4">15. Contact Us</h2>
            <p className="text-slate-600 leading-relaxed">
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="text-slate-600 mt-2">
              <strong>Email:</strong> support@corbel.work<br />
              <strong>Website:</strong> https://corbel.work
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t">
          <Link to="/privacy" className="text-orange-500 hover:underline">
            View Privacy Policy →
          </Link>
        </div>
      </main>
    </div>
  );
}
